import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const LoadingScreen = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1db584]"></div>
  </div>
);

/** Vérifie uniquement l'authentification (tous rôles). */
export const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return children;
};

/** Slugs réservés aux populations EXTERNES — exclues du staff. Les rôles
 *  custom créés via RBAC v2 (qui ne sont ni client ni chauffeur) sont
 *  automatiquement considérés comme staff et passent ce guard. */
const EXTERNAL_ROLES = new Set(['client', 'expediteur', 'chauffeur', 'driver']);

const STAFF_ROLES = [
  'admin',
  'lead_ops', 'ops', 'operateur',
  'lead_field_ops', 'field_ops',
  'lead_commercial', 'commercial',
  'finance',
  'auditeur',
];
const DRIVER_ROLES = ['chauffeur', 'driver'];

/** Route réservée aux expéditeurs/clients. Redirige staff → admin, driver → driver. */
export const ExpediteurRoute = ({ children }) => {
  const { user, isAuthenticated, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;

  const role = user?.role || '';
  if (DRIVER_ROLES.includes(role)) return <Navigate to="/chauffeur/dashboard" replace />;
  // Tout ce qui n'est ni client ni driver = staff (incl. rôles custom V2)
  if (!['client', 'expediteur'].includes(role)) return <Navigate to="/admin/dashboard" replace />;
  return children;
};

/** Route réservée aux chauffeurs. */
export const ChauffeurRoute = ({ children }) => {
  const { user, isAuthenticated, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;

  const role = user?.role || '';
  if (!DRIVER_ROLES.includes(role)) return <Navigate to="/dashboard" replace />;
  return children;
};

/** Route réservée au staff admin. RBAC v2 — accepte tout rôle non-client/driver,
 *  y compris les rôles custom créés dynamiquement. Si `allowedRoles` est passé,
 *  on revient au comportement strict (whitelist explicite). */
export const AdminRoute = ({ children, allowedRoles: customRoles }) => {
  const { user, isAuthenticated, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!isAuthenticated) return <Navigate to="/admin/login" replace />;

  const role = (user?.role || '').toLowerCase().trim();

  // Mode strict : whitelist explicite (rare — utilisé pour cloisonner certains screens)
  if (customRoles && customRoles.length > 0) {
    if (!customRoles.includes(role)) return <Navigate to="/admin/dashboard" replace />;
    return children;
  }

  // Mode RBAC v2 par défaut : autorise tout staff (= NOT client / NOT driver).
  // Le filtrage fin (par permission) est ensuite effectué :
  //   - côté sidebar : usePermissions().hasPermission('xxx.view')
  //   - côté backend : Depends(require_permission('xxx.view'))
  if (EXTERNAL_ROLES.has(role)) {
    // Client → /dashboard, Driver → /chauffeur/dashboard
    if (DRIVER_ROLES.includes(role)) return <Navigate to="/chauffeur/dashboard" replace />;
    return <Navigate to="/dashboard" replace />;
  }
  return children;
};
